import { z } from 'zod';
import { accountFilterSchema, idArray, subscriberTypeSchema } from './shared.schema';

export const invoiceMonthlyDetailFeeTypeSchema = z.enum([
	'Monthly',
	'Manual Monthly',
	'Activation',
	'Access Device',
	'Credit Card',
	'Late',
	'Recurring Late',
	'Transaction',
	'Mail Invoice',
	'Vehicle Surcharge',
	'Lost Device',
	'Beginning Balance',
	'Validation',
	'Hotel Storage',
	'Office/Retail Rent',
	'Miscellaneous',
]);

export const invoiceMonthlyDetailFeeTypes = invoiceMonthlyDetailFeeTypeSchema.options;

export type InvoiceMonthlyDetailFeeType = z.infer<typeof invoiceMonthlyDetailFeeTypeSchema>;

export const invoiceMonthlyDetailDefaultFeeTypes = [
	'Monthly',
	'Manual Monthly',
	'Activation',
	'Access Device',
	'Credit Card',
	'Late',
	'Recurring Late',
	'Transaction',
	'Mail Invoice',
	'Vehicle Surcharge',
] as const satisfies readonly InvoiceMonthlyDetailFeeType[];

export const invoiceMonthlyDetailReportFormatSchema = z.enum(['csv', 'pdf']);

export const invoiceMonthlyDetailParametersSchema = z
	.object({
		accountFilter: accountFilterSchema.optional(),
		subscriberType: subscriberTypeSchema.optional(),
		feeTypes: z.array(invoiceMonthlyDetailFeeTypeSchema).min(1),
		invoiceStartDate: z.iso.date().optional(),
		invoiceEndDate: z.iso.date().optional(),
		parkingPeriodStartDate: z.iso.date().optional(),
		parkingPeriodEndDate: z.iso.date().optional(),
		areaIds: idArray.optional(),
		branchIds: idArray.optional(),
		districtIds: idArray.optional(),
		facilityIds: z.array(z.string()).optional(),
		format: invoiceMonthlyDetailReportFormatSchema.default('csv'),
		facilityNumber: z.union([z.string(), z.number()]).optional(),
	})
	.superRefine((value, ctx) => {
		const hasInvoiceStartDate = value.invoiceStartDate !== undefined;
		const hasParkingPeriodStart = value.parkingPeriodStartDate !== undefined;

		if (!hasInvoiceStartDate && !hasParkingPeriodStart) {
			ctx.addIssue({
				code: 'custom',
				path: ['invoiceStartDate'],
				message: 'Either invoiceStartDate or parkingPeriodStartDate is required',
			});
		}

		if (value.invoiceEndDate && !hasInvoiceStartDate) {
			ctx.addIssue({
				code: 'custom',
				path: ['invoiceEndDate'],
				message: 'Cannot specify invoiceEndDate without invoiceStartDate',
			});
		}

		if (value.parkingPeriodEndDate && !hasParkingPeriodStart) {
			ctx.addIssue({
				code: 'custom',
				path: ['parkingPeriodEndDate'],
				message: 'Cannot specify parkingPeriodEndDate without parkingPeriodStartDate',
			});
		}

		if (
			value.parkingPeriodStartDate &&
			value.parkingPeriodEndDate &&
			value.parkingPeriodEndDate < value.parkingPeriodStartDate
		) {
			ctx.addIssue({
				code: 'custom',
				path: ['parkingPeriodEndDate'],
				message: 'Parking period end must be on or after parking period start',
			});
		}
	});

export type InvoiceMonthlyDetailParameters = z.infer<typeof invoiceMonthlyDetailParametersSchema>;
export type InvoiceMonthlyDetailReportFormat = z.infer<typeof invoiceMonthlyDetailReportFormatSchema>;
